import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { CheckCircle2, Clock, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const invoiceId = params.get("invoice_id");
  const [state, setState] = useState({ status: "checking", payment_status: "" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    if (state.payment_status === "paid") return;
    if (attempts >= 8) {
      setState({ status: "timeout", payment_status: "" });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setState(data);
        if (data.payment_status !== "paid" && data.status !== "expired") {
          setAttempts((a) => a + 1);
        }
      } catch {
        setAttempts((a) => a + 1);
      }
    }, attempts === 0 ? 800 : 2000);
    return () => clearTimeout(t);
  }, [attempts, sessionId, state.payment_status]);

  const isPaid = state.payment_status === "paid";
  const isExpired = state.status === "expired";

  return (
    <DashboardLayout role="client" title="Payment" subtitle="Confirming your payment with Stripe.">
      <div className="card p-10 max-w-2xl">
        {isPaid ? (
          <Result icon={CheckCircle2} color="var(--accent)" title="Payment received."
            body="Thank you. Your invoice has been marked paid." testid="pay-success" />
        ) : isExpired ? (
          <Result icon={XCircle} color="#fca5a5" title="Session expired."
            body="That payment session expired. You can try again from the invoices page." testid="pay-expired" />
        ) : (
          <Result icon={Clock} color="var(--text-muted)" title="Confirming payment…"
            body={`Polling Stripe for status (attempt ${attempts + 1} / 8).`} testid="pay-checking" />
        )}
        <div className="mt-6 flex gap-3">
          <Link to="/portal/invoices" className="btn-ghost">Back to invoices</Link>
          <Link to="/portal" className="btn-primary">Go to dashboard</Link>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Result({ icon: Icon, color, title, body, testid }) {
  return (
    <div data-testid={testid}>
      <Icon size={56} style={{color}} className="mb-4" />
      <h2 className="font-display text-3xl font-bold mb-2">{title}</h2>
      <p style={{color:'var(--text-muted)'}}>{body}</p>
    </div>
  );
}
